/**
 * InnerVoice marketing landing — תוכן HE / EN / ES
 * מבנה מורחב לכרטיסיות, מוקאפים ואייקונים.
 */

import { PROBLEM_GROUPS, getProblemTitleForLang } from "../journeyProblems";

/** סדר גלולות בסקשן המסעות + התאמה לקבוצות/נושאים מ־journeyProblems (מקור האמת באפליקציה) */
const LANDING_JOURNEY_CATALOG = [
  { kind: "group", key: "anxiety" },
  { kind: "problem", key: "overthinking_chronic_worry" },
  { kind: "problem", key: "rel_breakup_fresh_pain" },
  { kind: "problem", key: "fear_of_rejection" },
  { kind: "problem", key: "low_self_esteem" },
  { kind: "group", key: "loneliness_belonging" },
  { kind: "group", key: "depression_emptiness_meaning" },
  { kind: "problem", key: "lack_of_trust_in_relationship" },
  { kind: "problem", key: "feeling_disconnected_from_the_family_children" },
  { kind: "problem", key: "fear_of_failure" },
  { kind: "problem", key: "fear_meeting_people_social_events" },
  { kind: "problem", key: "financial_anxiety_debts" },
  { kind: "group", key: "loss_grief_radical_changes" },
  { kind: "problem", key: "the_feeling_of_life_passed_me_by" },
  {
    kind: "literal",
    he: "קושי בקבלת החלטות",
    en: "Difficulty making decisions",
    es: "Dificultad para decidir",
  },
  { kind: "problem", key: "harsh_self_criticism" },
  { kind: "problem", key: "health_anxiety_every_symptom_a_movie_in_the_head" },
];

function buildJourneysList(lang) {
  const safe = String(lang || "en").toLowerCase();
  return LANDING_JOURNEY_CATALOG.map((item) => {
    if (item.kind === "group") {
      const g = PROBLEM_GROUPS.find((x) => x.key === item.key);
      return g?.labels?.[safe] || g?.labels?.en || g?.labels?.he || item.key;
    }
    if (item.kind === "problem") {
      return getProblemTitleForLang(item.key, safe, { includeDuration: false });
    }
    if (item.kind === "literal") {
      return item[safe] || item.en;
    }
    return "";
  }).filter(Boolean);
}

export const IV_LANDING = {
  en: {
    htmlLang: "en",
    dir: "ltr",
    brand: "InnerVoice",
    heroTitle: "Meet yourself.",
    heroSubtitle: "A calm, structured journey inward—writing, reflection, one day at a time.",
    heroBadge: "What is InnerVoice?",
    heroCta: "Start your journey",
    heroTrustLine: "Start quickly: choose your topic, then begin your daily journey.",
    heroAfterClick: "After you click: choose what you're going through, then begin a guided daily journey.",
    bookCrosslink: "Not Broken Forever — the book",
    whatTitle: "What is InnerVoice?",
    whatCards: [
      {
        title: "Structured, not a chat",
        body: "Clear daily steps—not endless scrolling.",
      },
      {
        title: "Reflection space",
        body: "Self-paced (not clinical care).",
      },
      {
        title: "Write · reflect · understand",
        body: "Your own answers emerge quietly.",
      },
      {
        title: "A guided journey",
        body: "Stay with what you feel—without pressure.",
      },
    ],
    practicalTitle: "What you get in practice",
    practicalItems: [
      "Topic-based emotional journeys",
      "Daily guided steps",
      "Structured writing prompts",
      "Reflection and pattern-mapping",
      "A steady process back to yourself",
    ],
    trustTitle: "Why trust this process",
    trustItems: [
      "Built as a structured self-work method, not endless chat.",
      "Covers many recurring emotional themes people face every day.",
      "Designed for personal inner work (not a replacement for therapy).",
      "Created from real lived experience and long-form emotional writing.",
    ],
    whyTitle: "Why InnerVoice?",
    whyLead: "When you want clarity—not more noise.",
    whyCards: [
      {
        title: "Mental noise",
        body: "Thoughts that won’t switch off.",
      },
      {
        title: "Hard to name",
        body: "Something heavy, but fuzzy.",
      },
      {
        title: "Overload",
        body: "Too much feeling, not enough ground.",
      },
      {
        title: "Same loop",
        body: "A change or loss that keeps echoing.",
      },
    ],
    howTitle: "How it works",
    howSteps: [
      {
        n: "1",
        title: "Choose what you are going through",
        text: "Anxiety, breakups, overthinking, self-worth, and more.",
      },
      {
        n: "2",
        title: "Follow a daily guided journey",
        text: "Reflections, writing prompts, and small emotional steps.",
      },
      {
        n: "3",
        title: "Start understanding yourself",
        text: "Patterns clarify. Thoughts settle. Feelings get easier to hold.",
      },
    ],
    notAiTitle: "Not another AI chat",
    notAiColLeft: "ChatGPT",
    notAiColRight: "InnerVoice",
    notAiPairs: [
      { left: "Often gives quick answers in conversation.", right: "InnerVoice guides a structured journey to your own insights." },
      { left: "Open-ended chat.", right: "A daily process with clear steps." },
      { left: "Useful for many questions.", right: "Built for deeper self-understanding over time." },
    ],
    notAiClosing: "Not chatting.\nUnderstanding yourself.",
    previewTitle: "See InnerVoice in action",
    previewLead: "How it looks in the app.",
    previewScreens: [
      { key: "pick", title: "Choose what you’re facing", hint: "Problems & journeys" },
      { key: "day", title: "Your daily journey", hint: "Guidance for today" },
      { key: "write", title: "Writing", hint: "Prompts that meet you" },
      { key: "reflect", title: "Reflection", hint: "See what emerges" },
    ],
    journeysTitle: "Emotional journeys available",
    journeysIntro: "Tap what resonates—each journey is a structured path:",
    journeysList: buildJourneysList("en"),
    journeysMore: "And more…",
    journeysFoot: "Each journey is built as a structured emotional process, step by step.",
    whoTitle: "Who is InnerVoice for?",
    whoLead: "",
    whoList: [
      { icon: "mind", text: "Chronic overthinkers" },
      { icon: "pulse", text: "Anxiety & overload" },
      { icon: "heart", text: "Breakups & grief" },
      { icon: "compass", text: "Seeking self-understanding" },
      { icon: "sun", text: "Wanting clarity" },
      { icon: "pen", text: "Writing to think" },
    ],
    whoClosing: "No crisis required—just curiosity.",
    faqTitle: "Frequently asked questions",
    faqItems: [
      {
        q: "Is this an app or a website?",
        a: "InnerVoice works as a guided product experience with a structured daily flow.",
      },
      {
        q: "Is this instead of therapy?",
        a: "No. InnerVoice is a self-guided emotional process. It does not replace clinical care.",
      },
      {
        q: "Do I have to register before starting?",
        a: "You can start quickly and choose your topic first, then continue into your daily journey.",
      },
      {
        q: "How long is a journey?",
        a: "Most journeys are structured as daily steps over a focused period.",
      },
      {
        q: "Can I use it if I’m not used to writing a lot?",
        a: "Yes. The writing prompts are short, guided, and designed to meet you where you are.",
      },
    ],
    ctaTitle: "Start your inner journey today",
    ctaText: "You don’t need to have everything figured out.\nYou just need to start.",
    ctaButton: "Start your journey",
    journeyTagline: "InnerVoice isn’t just an app.\nInnerVoice is a journey.",
    footerTagline: "A place to think, write, reflect, and understand yourself.",
    footerPrivacy: "Privacy Policy",
    footerTerms: "Terms of Service",
    footerContact: "Contact",
  },

  he: {
    htmlLang: "he",
    dir: "rtl",
    brand: "InnerVoice",
    heroTitle: "לא כל דבר נפתר בעוד תשובה.\nיש דברים שצריך לעבור דרכם.",
    heroSubtitle: "מסע רגשי מובנה — עם כתיבה, שיקוף פנימי ותהליך יומי ברור.",
    heroBadge: "מה זה InnerVoice?",
    heroCta: "התחילו את המסע",
    heroTrustLine: "מתחילים מהר: בוחרים נושא ומתחילים מסע יומי.",
    heroAfterClick: "אחרי הלחיצה: בוחרים מה עובר עליכם עכשיו, ומתחילים מסע יומי מונחה.",
    bookCrosslink: "גם הספר: ׳לא שבור לנצח׳",
    pullQuoteAfterHero: "",
    pullQuoteAfterWhat: "",
    pullQuoteMid: "",
    whatTitle: "מה זה InnerVoice?",
    whatCards: [
      {
        title: "לא עוד צ'אט",
        body: "מסלול מובנה — בלי גלילה אינסופית ובלי תשובות מהירות שנזרקות אליכם.",
      },
      {
        title: "מסע רגשי מובנה",
        body: "מחשבות, רגשות ודפוסים — בתהליך יומי ברור, במקום אחד.",
      },
      {
        title: "בקצב שלכם",
        body: "לאט, בעדינות, ועם כיוון — בלי לחץ.",
      },
    ],
    practicalTitle: "מה מקבלים בפועל ב־InnerVoice",
    practicalItems: [
      "מסעות רגשיים לפי נושא",
      "צעדים יומיים ברורים",
      "שאלות כתיבה מודרכות",
      "שיקוף וזיהוי דפוסים",
      "תהליך מובנה לחזרה לעצמכם",
    ],
    trustTitle: "למה אפשר לסמוך על התהליך",
    trustItems: [
      "מבוסס על שיטת עבודה מובנית, לא על שיחה פתוחה בלי כיוון.",
      "כולל מגוון רחב של נושאים רגשיים נפוצים מהחיים עצמם.",
      "מותאם לעבודה עצמית פנימית (לא תחליף לטיפול).",
      "נבנה מתוך ניסיון אישי עמוק וכתיבה רגשית ארוכת־טווח.",
    ],
    whyTitle: "אם זה מוכר — אתם במקום הנכון",
    whyLead: "לא תמיד חסרה עוד עצה.\nלפעמים חסר מרחב להבין.",
    whyCards: [
      {
        title: "הראש לא נח",
        body: "מחשבות שלא מצליחות להיעצר.",
      },
      {
        title: "קשה לנסח",
        body: "יש משהו כבד בפנים, אבל קשה לשים עליו מילים.",
      },
      {
        title: "עומס וחוסר שקט",
        body: "הרבה רגש, מעט יציבות.",
      },
      {
        title: "משהו שחוזר",
        body: "שינוי, אכזבה או אובדן שעדיין מהדהדים בפנים.",
      },
    ],
    howTitle: "איך זה עובד?",
    howSteps: [
      {
        n: "1",
        title: "בוחרים את מה שעובר עליכם עכשיו",
        text: "חרדה, פרידה, מחשבות חוזרות, ערך עצמי — ועוד.",
      },
      {
        n: "2",
        title: "נכנסים למסע יומי מובנה",
        text: "תוכן יומי, שאלות כתיבה, ושיקוף קצר.",
      },
      {
        n: "3",
        title: "מתחילים להבין את עצמכם אחרת",
        text: "פחות בלבול.\nיותר בהירות לגבי מה שקורה בפנים.",
      },
    ],
    notAiTitle: "לא עוד צ'אט של בינה מלאכותית",
    notAiColLeft: "ChatGPT",
    notAiColRight: "InnerVoice",
    notAiPairs: [
      { left: "לרוב נותן תשובות מהירות בשיחה.", right: "InnerVoice מוביל מסע מובנה לתובנות שלכם." },
      { left: "שיחה פתוחה.", right: "תהליך יומי עם שלבים ברורים." },
      { left: "מתאים לשאלות רבות.", right: "בנוי להבנה עצמית עמוקה לאורך זמן." },
    ],
    notAiClosing: "לא צ'אט.\nתהליך של הבנה עצמית.",
    benefitsTitle: "מה InnerVoice נותן לכם?",
    benefitsLines: [
      "יותר חיבור פנימי, פחות בלבול.",
      "יותר מילים לרגש, פחות תחושת אובדן בתוך עצמכם.",
      "יותר תהליך, פחות הצפה.",
      "יותר אתם, פחות רעש מבחוץ.",
    ],
    previewTitle: "InnerVoice בפעולה",
    previewLead: "כך זה נראה באפליקציה.",
    previewScreens: [
      { key: "pick", title: "בחרו נושא", hint: "בחירה שמתאימה למה שעובר עליכם עכשיו." },
      { key: "day", title: "התחילו מסע", hint: "יום אחר יום, בתוך מסגרת ברורה." },
      { key: "write", title: "כתבו", hint: "שאלות שמחברות אתכם למה שעולה בפנים." },
      { key: "reflect", title: "התבוננו", hint: "כדי לראות טוב יותר מה קורה בתוככם." },
    ],
    journeysTitle: "מסעים רגשיים זמינים",
    journeysIntro: "דוגמאות לנושאים שאפשר לבחור — הרשימה המלאה נמצאת באפליקציה.",
    journeysList: [
      "חרדות ופחדים",
      "אובר־ת'ינקינג ודאגנות כרונית",
      "כאב של פרידה טרייה",
      "פחד מדחייה",
      "ערך עצמי נמוך",
      "בדידות ושייכות",
      "דיכאון, ריקנות ושאלות של משמעות",
      "חוסר אמון בעקבות בגידה, שקרים או סודות",
      "תחושת נתק מהמשפחה או מהילדים",
      "פחד מכישלון",
      "פחד ממפגש עם אנשים או אירועים חברתיים",
      "חרדה כלכלית וחובות",
      "אובדן, אבל ושינויים קיצוניים",
      "תחושה שהחיים עברו לידכם",
      "קושי בקבלת החלטות",
      "ביקורת עצמית קשה",
      "חרדת בריאות",
    ],
    journeysMore: "ועוד...",
    journeysFoot: "לכל מסע יש דרך.\nולכל דרך יש התחלה.",
    pullQuoteBeforeWho: "",
    whoTitle: "למי זה מתאים?",
    whoLead: "",
    whoList: [
      { icon: "mind", text: "מחשבות חוזרות" },
      { icon: "pulse", text: "חרדה ועומס" },
      { icon: "heart", text: "פרידה ואובדן" },
      { icon: "sun", text: "חיפוש אחר בהירות" },
      { icon: "compass", text: "רצון להבין את עצמכם טוב יותר" },
      { icon: "pen", text: "כתיבה ככלי לחשיבה" },
    ],
    whoClosing: "לא חייבים להיות במשבר.\nמספיק לרצות להקשיב לעצמכם אחרת.",
    faqTitle: "שאלות נפוצות",
    faqItems: [
      {
        q: "זו אפליקציה או אתר?",
        a: "InnerVoice עובד כמוצר מונחה עם זרימה יומית ברורה.",
      },
      {
        q: "זה במקום טיפול?",
        a: "לא. InnerVoice הוא תהליך עבודה עצמית רגשית, והוא לא מחליף טיפול מקצועי.",
      },
      {
        q: "חייבים להירשם לפני שמתחילים?",
        a: "אפשר להתחיל מהר בבחירת מה שעובר עליכם, ואז להמשיך למסע היומי.",
      },
      {
        q: "כמה זמן לוקח מסע?",
        a: "רוב המסעות בנויים כתהליך יומי לתקופה ממוקדת.",
      },
      {
        q: "זה מתאים גם למי שלא כותב הרבה?",
        a: "כן. הכתיבה קצרה, מונחית, ומותאמת גם למי שלא רגיל לכתוב.",
      },
      {
        q: "מה ההבדל בין InnerVoice לבין ChatGPT?",
        a: "ChatGPT מתאים לשיחה ותשובות מהירות. InnerVoice מוביל תהליך מובנה שמפתח הבנה פנימית לאורך זמן.",
      },
    ],
    ctaTitle: "לא צריך להבין הכול מראש.\nצריך רק להתחיל.",
    ctaText: "לא צריך להבין הכול מראש.\nלא צריך להיות מוכנים לגמרי.\nצריך רק להתחיל.",
    ctaButton: "התחילו את המסע",
    journeyTagline: "",
    footerTagline: "מקום לחשוב, לכתוב, להתבונן, ולהבין את עצמכם.",
    footerPrivacy: "מדיניות פרטיות",
    footerTerms: "תנאי שימוש",
    footerContact: "יצירת קשר",
  },

  es: {
    htmlLang: "es",
    dir: "ltr",
    brand: "InnerVoice",
    heroTitle: "Encuéntrate contigo mismo.",
    heroSubtitle: "Un viaje emocional serio—escritura, reflexión, un día a la vez.",
    heroBadge: "Qué es InnerVoice",
    heroCta: "Comienza tu viaje",
    heroTrustLine: "Empieza rápido: elige tu tema y comienza tu viaje diario.",
    heroAfterClick: "Después del clic: eliges lo que te está pasando y comienzas un viaje diario guiado.",
    bookCrosslink: "También el libro: «No roto para siempre»",
    whatTitle: "¿Qué es InnerVoice?",
    whatCards: [
      {
        title: "Serio, no chat",
        body: "Pasos claros—sin scroll infinito.",
      },
      {
        title: "Espacio de reflexión",
        body: "A tu ritmo (no es servicio clínico).",
      },
      {
        title: "Escribe · reflexiona · comprende",
        body: "Tus respuestas aparecen en calma.",
      },
      {
        title: "Viaje guiado",
        body: "Sostén lo que sientes, sin prisa.",
      },
    ],
    practicalTitle: "Qué obtienes en la práctica",
    practicalItems: [
      "Viajes emocionales por temas",
      "Pasos diarios guiados",
      "Preguntas de escritura estructurada",
      "Reflexión y reconocimiento de patrones",
      "Un proceso claro para volver a ti",
    ],
    trustTitle: "Por qué confiar en este proceso",
    trustItems: [
      "Construido como método estructurado, no como chat sin rumbo.",
      "Incluye muchos temas emocionales reales y frecuentes.",
      "Diseñado para trabajo personal (no reemplaza terapia).",
      "Creado desde experiencia emocional real y trabajo profundo.",
    ],
    whyTitle: "Por qué InnerVoice",
    whyLead: "Cuando buscas claridad — no más ruido.",
    whyCards: [
      {
        title: "Ruido mental",
        body: "Pensamientos que no paran.",
      },
      {
        title: "Difícil de nombrar",
        body: "Algo pesado, pero difuso.",
      },
      {
        title: "Sobrecarga",
        body: "Mucha emoción, poco suelo.",
      },
      {
        title: "Mismo bucle",
        body: "Un cambio o pérdida que sigue resonando.",
      },
    ],
    howTitle: "Cómo funciona",
    howSteps: [
      {
        n: "1",
        title: "Elige por lo que estás pasando",
        text: "Ansiedad, ruptura, rumiación, autoestima y más.",
      },
      {
        n: "2",
        title: "Sigue un viaje guiado cada día",
        text: "Reflexiones, prompts de escritura y pasos pequeños.",
      },
      {
        n: "3",
        title: "Empieza a entenderte",
        text: "Los patrones se aclaran. Lo mental asienta. Lo emocional se sostiene mejor.",
      },
    ],
    notAiTitle: "No es otro chat de IA",
    notAiColLeft: "ChatGPT",
    notAiColRight: "InnerVoice",
    notAiPairs: [
      { left: "Suele dar respuestas rápidas en conversación.", right: "InnerVoice guía un viaje estructurado hacia tus propias ideas." },
      { left: "Chat abierto.", right: "Un proceso diario con pasos claros." },
      { left: "Útil para muchas preguntas.", right: "Diseñado para una comprensión más profunda con el tiempo." },
    ],
    notAiClosing: "No es charla.\nEs comprenderte.",
    previewTitle: "InnerVoice en acción",
    previewLead: "Así se ve en la app.",
    previewScreens: [
      { key: "pick", title: "Elige lo que te pasa", hint: "Problemas y viajes" },
      { key: "day", title: "Tu viaje del día", hint: "Guía para hoy" },
      { key: "write", title: "Escritura", hint: "Prompts que te acompañan" },
      { key: "reflect", title: "Reflexión", hint: "Mira lo que aparece" },
    ],
    journeysTitle: "Viajes emocionales disponibles",
    journeysIntro: "Toca lo que resuena — cada viaje es un camino estructurado:",
    journeysList: buildJourneysList("es"),
    journeysMore: "Y más…",
    journeysFoot: "Cada viaje está construido como un proceso emocional paso a paso.",
    whoTitle: "¿Para quién es InnerVoice?",
    whoLead: "",
    whoList: [
      { icon: "mind", text: "Rumiación" },
      { icon: "pulse", text: "Ansiedad y carga" },
      { icon: "heart", text: "Rupturas y duelo" },
      { icon: "compass", text: "Quieren entenderse" },
      { icon: "sun", text: "Buscan claridad" },
      { icon: "pen", text: "Escribir para pensar" },
    ],
    whoClosing: "Sin crisis—solo curiosidad.",
    faqTitle: "Preguntas frecuentes",
    faqItems: [
      {
        q: "¿Es una app o un sitio web?",
        a: "InnerVoice funciona como una experiencia guiada con un flujo diario claro.",
      },
      {
        q: "¿Reemplaza terapia?",
        a: "No. Es un proceso de trabajo personal y no sustituye atención clínica.",
      },
      {
        q: "¿Tengo que registrarme antes de empezar?",
        a: "Puedes empezar rápido eligiendo lo que te pasa y luego continuar tu recorrido diario.",
      },
      {
        q: "¿Cuánto dura un viaje?",
        a: "La mayoría están estructurados como pasos diarios en un periodo enfocado.",
      },
      {
        q: "¿Sirve si no escribo mucho?",
        a: "Sí. Los prompts son breves, guiados y accesibles.",
      },
    ],
    ctaTitle: "No necesitas entenderlo todo primero.\nSolo necesitas empezar.",
    ctaText: "No necesitas tenerlo todo resuelto.\nSolo necesitas empezar.",
    ctaButton: "Comenzar el viaje",
    journeyTagline: "InnerVoice no es solo una app.\nInnerVoice es un viaje.",
    footerTagline: "Un lugar para pensar, escribir, reflexionar y entenderte.",
    footerPrivacy: "Política de privacidad",
    footerTerms: "Términos de servicio",
    footerContact: "Contacto",
  },
};
